import { useStorytellClient } from "../storytellClient";
import type {
  ChatMembersResponse,
  ChatTitleResponse,
  ChatVisibilityLevel,
  MoveChatsRequest,
  SearchChatsParams,
  SearchChatsResponse,
  SendShareEmailsResponse,
  SetChatVisibilityResponse,
  ShareStatusResponse,
} from "./chatClient.api";
import {
  CreateShareLinkRequestConfig,
  DeleteChatRequestConfig,
  DeleteMessageRequestConfig,
  GetChatMembersRequestConfig,
  GetShareStatusRequestConfig,
  ImprovePromptRequestConfig,
  MoveChatsRequestConfig,
  RegenerateChatTitleRequestConfig,
  RevokeShareLinkRequestConfig,
  SearchChatsRequestConfig,
  SendShareEmailsRequestConfig,
  SetChatVisibilityRequestConfig,
  UpdateChatTitleRequestConfig,
} from "./chatClient.api";
import type { ImprovePromptBody, ImprovePromptResponse } from "./chatClient.types";

class ChatClient {
  private token: string | undefined;
  private client = useStorytellClient();

  constructor(token?: string) {
    this.token = token;
  }

  /**
   * Delete a chat
   * @param id - The chat ID to delete
   * @returns Promise that resolves when chat is deleted
   */
  async deleteChat(id: string): Promise<void> {
    const config = DeleteChatRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, { id }, undefined);

    if (result.isErr()) {
      throw result.error;
    }
  }

  /**
   * Delete a single message (prompt + response) in a chat.
   * Fails with conflict if the message is currently streaming.
   */
  async deleteMessage(chatId: string, messageId: string): Promise<void> {
    const config = DeleteMessageRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, { id: chatId, messageId }, undefined);

    if (result.isErr()) {
      throw result.error;
    }
  }

  async moveChats(chats: MoveChatsRequest["chats"]): Promise<void> {
    const config = MoveChatsRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, undefined, { chats });

    if (result.isErr()) {
      throw result.error;
    }
  }

  /**
   * Update chat title
   * @param chatId - The chat ID to update
   * @param title - The new title (max 255 characters)
   * @returns Promise with updated chat metadata
   */
  async updateChatTitle(chatId: string, title: string): Promise<ChatTitleResponse> {
    const config = UpdateChatTitleRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, { id: chatId }, { title });

    if (result.isErr()) {
      throw result.error;
    }

    return result.value;
  }

  /**
   * Regenerate chat title using LLM
   * @param chatId - The chat ID to regenerate title for
   * @returns Promise with updated chat metadata including new LLM-generated title
   */
  async regenerateChatTitle(chatId: string): Promise<ChatTitleResponse> {
    const config = RegenerateChatTitleRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, { id: chatId }, undefined);

    if (result.isErr()) {
      throw result.error;
    }

    return result.value;
  }

  /**
   * Get the public sharing status for a chat
   * @param chatId - The chat ID to check
   * @returns Promise with sharing status
   */
  async getShareStatus(chatId: string): Promise<ShareStatusResponse> {
    const config = GetShareStatusRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, { id: chatId }, undefined);

    if (result.isErr()) {
      throw result.error;
    }

    return result.value;
  }

  /**
   * Create a public share link for a chat
   * @param chatId - The chat ID to share
   * @param regenerate - Whether to regenerate if a link already exists
   * @returns Promise with share status including the token
   */
  async createShareLink(chatId: string): Promise<ShareStatusResponse> {
    const config = CreateShareLinkRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, { id: chatId }, undefined);

    if (result.isErr()) {
      throw result.error;
    }

    return result.value;
  }

  /**
   * Revoke a public share link for a chat
   * @param chatId - The chat ID to revoke sharing for
   * @returns Promise that resolves when link is revoked
   */
  async revokeShareLink(chatId: string): Promise<void> {
    const config = RevokeShareLinkRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, { id: chatId }, undefined);

    if (result.isErr()) {
      throw result.error;
    }
  }

  /**
   * Send share link emails to specified recipients
   * @param chatId - The chat ID to share
   * @param emails - Array of email addresses (max 10)
   * @param textFragment - Optional text fragment to append to URL (e.g., "#:~:text=...")
   * @returns Promise with send results
   */
  async sendShareEmails(chatId: string, emails: string[], textFragment?: string): Promise<SendShareEmailsResponse> {
    const config = SendShareEmailsRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, { id: chatId }, { emails, textFragment });

    if (result.isErr()) {
      throw result.error;
    }

    return result.value;
  }

  /**
   * Pass `memberIds` only when `visibility` is "restricted"; the server
   * clears the audience for the other levels. Owner/admin only.
   */
  async setVisibility(
    chatId: string,
    input: { visibility: ChatVisibilityLevel; memberIds?: string[] }
  ): Promise<SetChatVisibilityResponse> {
    const config = SetChatVisibilityRequestConfig.withBearerToken(this.token);
    const body =
      input.visibility === "restricted"
        ? { visibility: input.visibility, memberIds: input.memberIds ?? [] }
        : { visibility: input.visibility };
    const result = await this.client.fetch(config, { id: chatId }, body);

    if (result.isErr()) {
      throw result.error;
    }

    return result.value;
  }

  /**
   * List the audience member ids for a restricted chat. Owner/admin only.
   */
  async getChatMembers(chatId: string): Promise<ChatMembersResponse> {
    const config = GetChatMembersRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, { id: chatId }, undefined);

    if (result.isErr()) {
      throw result.error;
    }

    return result.value;
  }

  async improvePrompt(body: ImprovePromptBody): Promise<ImprovePromptResponse> {
    const config = ImprovePromptRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, undefined, body);

    if (result.isErr()) {
      throw result.error;
    }

    return result.value;
  }

  /**
   * Full-text search across chats (project-scoped, requires `chat.search` entitlement).
   * Throws on 400 (missing query) or 403 (feature gated); callers should branch on `error.code`.
   */
  async searchChats(params: SearchChatsParams): Promise<SearchChatsResponse> {
    const config = SearchChatsRequestConfig.withBearerToken(this.token);
    const result = await this.client.fetch(config, params, undefined);

    if (result.isErr()) {
      throw result.error;
    }

    return result.value;
  }
}

/**
 * Create a new ChatClient instance
 * @param token - Optional bearer token for authentication
 * @returns ChatClient instance
 */
export const createChatClient = (token?: string): ChatClient => {
  return new ChatClient(token);
};
